import { useState } from 'react'
import {
	Row,
	Col,
	Card,
	Image,
	InputGroup,
	FormControl
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import Link from 'next/link'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import styles from '../styles/BrowseEmployers.module.scss'
import { useRouter } from 'next/router'

const Employers = ({ filters, companies, pagination, companyAggregate }) => {
	const router = useRouter()
	const locale: string = router.locale as string

	const [companyFilters, setCompanyFilters] = useState(filters)
	const { t } = useTranslation()
	companies = companies.filter((c) => {
		// only show employers with jobs
		return !([5066, 5056, 5376].includes(c.id)) && c.Jobs?.length !== 0
	})

	const handleInputChange = (event: any): any => {
		const target = event.target
		const value = target.value
		const name = target.name

		setCompanyFilters({
			...companyFilters,
			[name]: value
		})
	}

	const handleSubmit = (): void => {
		redirectNewUrl()
	}

	const handleKeyDown = (e: any): void => {
		if (e.keyCode === 13) {
			redirectNewUrl()
		}
	}

	const redirectNewUrl = (): void => {
		const keyword: string = (companyFilters.keyword as string).replace(/[`~!@#$%^&*()_|+\-=?;:'".<>\{\}\[\]\\\/]/gi, '')
		setCompanyFilters({
			...companyFilters,
			keyword: keyword
		})
		router.push(`/${locale}/browse-employers/?page=1&q=${encodeURIComponent(keyword)}`).catch(() => null)
	}

	const getPaginationLink = (page: string): string => {
		const keyword: string = companyFilters.keyword as string
		return `/${locale}/browse-employers/?page=${page}&q=${encodeURIComponent(keyword)}`
	}

	return (
		<>
			<Row className='mt-2'>
				<Col>
					<h2>{t('jobs.these_employers_are_hiring')}</h2>
				</Col>
			</Row>
			<br />
			<Row>
				<Col>
					<InputGroup className="mb-2">
						<FormControl
							name='keyword'
							placeholder={t('search_for_company')}
							aria-label={t('search_for_company')}
							aria-describedby={t('search_for_company')}
							size='lg'
							value={companyFilters.keyword}
							onChange={handleInputChange}
							onKeyDown={handleKeyDown}
						/>
						<InputGroup.Text className={styles.inputGroupText} id="basic-addon1" onClick={handleSubmit}>
							<FontAwesomeIcon icon={faSearch} />
						</InputGroup.Text>
					</InputGroup>
				</Col>
			</Row>
			{companies.length !== 0 ?
				<Row className='gy-3'>
					{companies.map((c, i) => {
						return (
							<Col lg={3} xs={6} key={i}>
								<Link href={`/company/jobs/${c.id}`} passHref>
									<a className='text-decoration-none' target='_blank'>
										<Card className='mt-2' style={{ minHeight: '230px' }}>
											<Card.Body className='text-center w-100 pt-4 align-middle '>
												<div className='w-100'>
													{!c.avatar && <div className={styles.noAvatarImage}>{c.name}</div>}
													{c.avatar && <Image src={c.avatar} className={styles.avatarImage} alt={c.name.substring(0, 10)} />}
												</div>
												<p className='mt-3 text-dark'>{c.name}</p>
												{/* <small className='text-success'>( {c.Jobs_aggregate.aggregate.count} {t('jobs.available_jobs_count')} )</small> */}
											</Card.Body>
										</Card>
									</a>
								</Link>
							</Col>
						)
					})}
				</Row>
				:
				<Row>
					<Col className='text-center'>
						<br />
						<p>{t('employers.not_found_company')}</p>
						<p>{t('employers.try_different_keyword')}<Link href='/browse-employers'>{t('register.click_here')}</Link>{t('employers.back_to_company_list')}</p>
					</Col>
				</Row>
			}

			{(companies.length > 0 && companyAggregate > 10 ) ?
				<div className={styles.paginationContainer}>
        	<nav aria-label='Page navigation example'>
        		<ul className='pagination justify-content-center pagination-md'>
        			<li className={pagination.prev ? 'page-item' : 'page-item disabled'}>
        				<a className='page-link' href={getPaginationLink((parseInt(filters.page) - 1).toString())} aria-disabled='true'>
        					<FontAwesomeIcon icon={faChevronLeft} width='12px' />
        				</a>
        			</li>
        			{pagination.pagi.map((pagi, index) => {
        				return (
        					<li key={index} className={pagi === 'dotted' ? 'page-item disabled' : (pagi === parseInt(filters.page) ? 'page-item active' : 'page-item')}>
        						<a className='page-link' href={getPaginationLink(pagi.toString())}>{pagi === 'dotted' ? '...' : pagi}</a>
        					</li>
        				)
        			})}
        			<li className={pagination.next ? 'page-item' : 'page-item disabled'}>
        				<a className='page-link' href={getPaginationLink((parseInt(filters.page) + 1).toString())}>
        					<FontAwesomeIcon icon={faChevronRight} width='12px' />
        				</a>
        			</li>
        		</ul>
        	</nav>
				</div>
				:
				<br />
			}
		</>
	)
}

export async function getServerSideProps({locale, query}) {
	const filters = {
		page: (typeof query.page !== 'undefined' && parseInt(query.page)) ? query.page : 1,
		keyword: (typeof query.q !== 'undefined' ? query.q as string : '').replace(/[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gi, ''),
	}

	const apiUrl = `${process.env.NEXT_PUBLIC_API_ENDPOINT}v1/jobseeker/employer?country=${locale?.toUpperCase()}&keyword=${filters.keyword}&page=${filters.page}&pageSize=16`
	const res = await fetch(apiUrl)
	const data = await res.json()

	const companies = [...data.rows]
	const companyAggregate = data.count
	
	type Pagination = {
	  prev: boolean,
	  pagi: any[],
	  next: boolean
	}

	const currentPage = parseInt(filters.page)
	const pagination: Pagination = {
		prev: false,
		pagi: [],
		next: false
	}
	if (currentPage > 1) {
		pagination.prev = true
	}

	const numOfPage = Math.ceil(companyAggregate / 16)

	if (numOfPage < 6) {
		pagination.pagi = Array.from(Array(numOfPage), (e, i) => { return i + 1 })
	} else {
		if (currentPage < 3) {
			pagination.pagi = Array.from(Array(3), (e, i) => { return i + 1 })
			pagination.pagi.push('dotted')
			pagination.pagi.push(numOfPage)
		} else {
			const tempPagiItems: any[] = []

			if (currentPage - 1 > 1) {
				if (currentPage - 2 !== 1) {
					tempPagiItems.push(1)
					if (currentPage - 2 !== 1) {
						tempPagiItems.push('dotted')
					}
				}
				// tempPagiItems.push(currentPage - 2)
			}

			if (currentPage - 1 > 1) {
				tempPagiItems.push(currentPage - 1)
			}

			tempPagiItems.push(currentPage)
			if (currentPage + 1 <= numOfPage) {
				tempPagiItems.push(currentPage + 1)
			}
			if (currentPage + 2 <= numOfPage) {
				// tempPagiItems.push(currentPage + 2)
				if (currentPage + 2 !== numOfPage) {
					if (currentPage + 3 < numOfPage) {
						tempPagiItems.push('dotted')
					}
					tempPagiItems.push(numOfPage)
				}
			}

			pagination.pagi = tempPagiItems
		}
	}

	pagination.next = false
	if (currentPage < numOfPage) {
		pagination.next = true
	}

	// Pass data to the page via props
	return {
  	props: {
  		...(await serverSideTranslations(locale, ['common', 'footer'])),
			filters,
  		companies,
			companyAggregate,
  		pagination
  	}
	}
}

export default Employers
